import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { SUPPORT_EMAIL, buildSupportEmailHref } from '../utils/supportContact';

/**
 * NeedHelpModal — reusable controlled pop-up that points users to support.
 *
 * Like TermsPrivacyModal, this is a *controlled* component: it owns no internal
 * open/close state. The caller maintains the boolean, wires a trigger button to
 * open it, and passes `open` / `onOpenChange` here.
 *
 * Behaviour:
 *   - Shows short instructions telling the user how to get help.
 *   - The support email address is itself a `mailto:` link. Clicking it opens
 *     the user's default email client with a pre-filled subject and a body that
 *     already includes diagnostic context (screen, URL, time, user/org,
 *     browser). There is deliberately NO free-text box in this modal — the
 *     description is captured in the email body instead, which keeps the modal
 *     simple and works even if the app itself is misbehaving.
 *
 * Props:
 *   open            {boolean}  — whether the dialog is currently open
 *   onOpenChange    {function} — called by Radix with the next open state
 *   userId          {string}   — current user's UID, embedded in the email context
 *   organisation    {string}   — current user's organisation, embedded in the email
 *   patientId         {string}   — (optional) UID of the patient currently being worked on
 *   referenceNumber   {string}   — (optional) the patient's reference number
 *   assessmentPathway {string}   — (optional) human-readable pathway label e.g. "Adult ADHD"
 */
export default function NeedHelpModal({
  open, onOpenChange, userId, organisation, patientId, referenceNumber, assessmentPathway,
}) {
  // Recompute the mailto each render so the embedded timestamp/URL reflect the
  // moment the user actually opens their email client (cheap to build).
  const mailtoHref = buildSupportEmailHref({
    userId, organisation, patientId, referenceNumber, assessmentPathway,
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />

        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-sm w-[calc(100vw-2rem)] z-50 outline-none">

          {/* Close button — top-right corner, accessible label for screen readers */}
          <Dialog.Close asChild>
            <button
              aria-label="Close"
              className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 text-xl leading-none transition-colors"
            >
              ×
            </button>
          </Dialog.Close>

          <Dialog.Title className="text-lg font-semibold text-gray-900 mb-1 text-left">
            Need help?
          </Dialog.Title>

          <Dialog.Description asChild>
            <div className="font-medium text-sm text-gray-600 mt-2 text-left">
              <p className="mb-3">
                Have a question, some feedback, or run into an issue? Email our
                support team and we will be happy to help.
              </p>
              <p>
                Get in touch at{' '}
                <a
                  href={mailtoHref}
                  className="text-primary hover:text-violet-800 underline font-semibold"
                >
                  {SUPPORT_EMAIL}
                </a>
                .
              </p>
              <p className="mt-3 text-xs text-gray-500">
                Clicking the link above will open your email app with some
                helpful details pre-filled. Just add a short description of your
                support request before sending.{' '}
                <span className="font-semibold">
                  Please do not include personal, identifiable information about
                  patients.
                </span>
              </p>
            </div>
          </Dialog.Description>

        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
